import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Handshake, Clock, Unlock, ChevronDown, ChevronUp } from 'lucide-react';
import { motion } from 'framer-motion';
import { PageBanner } from '../components/PageBanner';
import { AnimatedSection } from '../components/AnimatedSection';
import { openContactModal } from '../components/ContactModal';
import { PhotoCredit } from '../components/PhotoCredit';
type FAQItem = {
  id: string;
  question: string;
  answer: string | React.ReactNode;
};
type FAQCategory = {
  id: string;
  title: string;
  items: FAQItem[];
};
const faqData: FAQCategory[] = [{
  id: 'about',
  title: 'ABOUT THE FIRETREE FUND',
  items: [{
    id: 'q1',
    question: 'How is this different from other intermediaries or advisory platforms?',
    answer: <div className="space-y-4" data-id="element-292">
            <p data-id="element-293">
              Firetree does not operate as a marketplace or introduction
              service.
            </p>
            <p data-id="element-294">
              We fund first and share later. Every organisation in the Fund
              portfolio has been supported by Firetree through a multi-year
              relationship. Our judgment is shaped by sustained engagement, site
              visits, operational conversations, and real partnership, not
              proposal review alone. We also operate nonprofit programmes
              ourselves in education and child protection, ensuring that our
              evaluation of partners is grounded in lived practice rather than
              observation.
            </p>
            <p data-id="element-295">
              The Firetree Fund is not a replacement for our own funding. We
              continue to back every organisation in this portfolio directly, as
              we have for years. What the Fund offers other funders is something
              different: a way into organisations we know deeply, seen through
              the same lens we use ourselves. Rather than navigating the
              Southeast Asian nonprofit landscape from a distance, funders gain
              access to relationships, context, and operational understanding
              that took us years to build.
            </p>
            <p data-id="element-296">
              For funders newer to this region or to trust-based philanthropy,
              the Fund also offers a window into how we work: our approach to
              identifying organisations, building relationships over time, and
              making funding decisions grounded in practice rather than
              paperwork. We share not just who we fund, but why, and how we came
              to know them well enough to back them with confidence.
            </p>
          </div>
  }, {
    id: 'q2',
    question: 'Is the Firetree Fund a pooled fund?',
    answer: <div className="space-y-4" data-id="element-297">
            <p data-id="element-298">
              The Firetree Fund is a co-funding opportunity, not a vehicle where
              contributions disappear into a collective pot.
            </p>
            <p data-id="element-299">
              When you give through the Fund, you are joining Firetree in
              backing organisations we already fund ourselves. You will always
              know which organisations your contribution is supporting and
              receive communication accordingly.
            </p>
            <p data-id="element-300">
              Allocation decisions are made by the Firetree Board, advised by a
              regional panel of technical experts from across Southeast Asia.
              That is deliberate. The value of the Fund rests on the consistency
              and integrity of our judgment, built over years of real
              partnership with these organisations. Funders who participate are,
              in effect, trusting Firetree to apply that judgment on their
              behalf, the same judgment we apply to our own giving.
            </p>
          </div>
  }, {
    id: 'q3',
    question: 'Is Firetree a fundraiser?',
    answer: <div className="space-y-4" data-id="element-301">
            <p data-id="element-302">Firetree is a funder, not a fundraiser.</p>
            <p data-id="element-303">
              We are not running a drive to raise money on behalf of the
              organisations we support. What we are doing is simpler: sharing
              access to organisations we have found, vetted, and backed through
              our own funding, with other funders who are interested in the same
              issues and geographies.
            </p>
            <p data-id="element-304">
              We believe the time and energy of nonprofit leaders is best spent
              on their work and the communities they serve, not on running
              fundraising drives. If we, as existing supporters of these
              organisations, can help other aligned funders understand what we
              know about them and why we back them, then we have removed some of
              that burden without asking anything of the organisations
              themselves.
            </p>
            <p data-id="element-305">
              The Fund is an extension of how we already work. It is not a
              fundraising mechanism. It is an offer to share our view.
            </p>
          </div>
  }, {
    id: 'q4',
    question: 'Is the Firetree Fund registered as a legal entity? What is ACF?',
    answer: <div className="space-y-4" data-id="element-306">
            <p data-id="element-307">
              The Firetree Fund operates as an initiative of the Firetree Group.
              For funders who wish to give through a managed vehicle,
              contributions are housed on the Asia Community Foundation (ACF)
              platform, a registered philanthropic platform that handles the
              legal and administrative infrastructure. You can learn more about
              ACF at{' '}
              <a href="https://asiacf.org" target="_blank" rel="noopener noreferrer" className="text-brand-red hover:underline" data-id="element-308">
                asiacf.org
              </a>
              .
            </p>
            <p data-id="element-309">
              Firetree manages the allocation process and funder engagement.
              Funders contributing through ACF benefit from the platform's
              compliance and governance framework while Firetree retains
              responsibility for portfolio judgment.
            </p>
          </div>
  }, {
    id: 'q5',
    question: 'Is there a minimum contribution?',
    answer: <div className="space-y-4" data-id="element-310">
            <p data-id="element-311">
              Yes. The Firetree Fund is designed for value-aligned funders
              committed to long-term, unrestricted giving. The minimum
              contribution is USD 100,000 per year.
            </p>
            <p data-id="element-312">
              If you are interested in engaging with our portfolio organisations
              in a different capacity, we are happy to have a conversation and
              make direct introductions where it makes sense.
            </p>
          </div>
  }, {
    id: 'q6',
    question: 'Can the Fund accept contributions from outside Singapore or Asia?',
    answer: <div className="space-y-4" data-id="element-313">
            <p data-id="element-314">
              Yes. The Firetree Fund welcomes contributions from value-aligned
              funders globally. There is no geographic restriction on who can
              give through the Fund.
            </p>
            <p data-id="element-315">
              All new funders go through ACF's Know Your Donor process before
              contributions are accepted. This is a standard due diligence
              procedure covering the source of funds and anti-money laundering
              requirements. We would guide you through what that involves as
              part of our initial conversations.
            </p>
          </div>
  }]
}, {
  id: 'selection',
  title: 'HOW ORGANISATIONS ARE SELECTED AND THE PORTFOLIO',
  items: [{
    id: 'q7',
    question: 'How does Firetree identify and select organisations for the portfolio?',
    answer: <div className="space-y-4" data-id="element-316">
            <p data-id="element-317">
              There is no open application process. Organisations in the Fund
              portfolio are drawn exclusively from Firetree's own portfolio of
              partners, built over 20 years of direct funding and engagement in
              Southeast Asia.
            </p>
            <p data-id="element-318">
              For a full picture of how we identify, assess, and build
              relationships with organisations, visit our How We Partner page.
            </p>
          </div>
  }, {
    id: 'q8',
    question: 'Who approves which organisations enter the Fund portfolio?',
    answer: <p data-id="element-319">
            All portfolio inclusions are approved by the Firetree Board, advised
            by a regional panel of technical experts spanning child protection,
            education, and nonprofit operations across Southeast Asia, chaired
            by Nicky Wilkinson.
          </p>
  }, {
    id: 'q9',
    question: 'Are all the organisations in the portfolio working on child protection, or are there other focus areas?',
    answer: <div className="space-y-4" data-id="element-320">
            <p data-id="element-321">
              Child protection is the primary focus of the Firetree Fund and the
              area where Firetree has the deepest direct experience. That said,
              we care about organisations doing strong, grounded work across the
              geographies we operate in and in issues that are closely connected
              to the protection and development of children and families.
            </p>
            <p data-id="element-322">
              The portfolio therefore also includes organisations working on
              innovations in education. This reflects both the reality that
              child protection and education are deeply intertwined in the lives
              of the families we seek to support, and our own direct experience
              running Starfish Education Foundation, a wholly owned and operated
              initiative of Firetree in Thailand.
            </p>
          </div>
  }, {
    id: 'q10',
    question: 'What happens if an organisation in the portfolio faces a governance crisis or significant leadership change?',
    answer: <div className="space-y-4" data-id="element-323">
            <p data-id="element-324">
              In our experience, significant governance issues or organisational
              crises are rarely a surprise. Because of the frequency and depth
              of our engagement with portfolio organisations, we are almost
              always able to see early indicators of something changing or going
              wrong, long before it becomes a crisis. This is something we have
              navigated across a number of organisations over the years. In the
              rare event that something were to change significantly enough to
              warrant flagging to funders, we would do so directly and promptly.
            </p>
            <p data-id="element-325">
              It is also worth noting that the organisations in this portfolio
              have been selected precisely because of their history and
              strength. They have been through a rigorous assessment across
              programmes, operations, and leadership using the Firetree rubric,
              and have also demonstrated their resilience and integrity through
              years of real funding relationship with us. These are not
              organisations we have recently discovered. They are organisations
              we know well.
            </p>
            <p data-id="element-326">
              In the event that something were to change significantly, we would
              not handle it at a distance. We would have direct conversations
              with the organisation and, where appropriate, with funders
              themselves to ensure everyone has the context they need to respond
              thoughtfully.
            </p>
          </div>
  }]
}, {
  id: 'funding',
  title: 'HOW FUNDING WORKS',
  items: [{
    id: 'q11',
    question: 'Can I direct my contribution to a specific organisation or country?',
    answer: <div className="space-y-4" data-id="element-327">
            <p data-id="element-328">
              Funders who contribute through the ACF-managed vehicle allow
              Firetree to allocate across the portfolio based on ongoing
              engagement with each organisation, their current needs, and their
              potential to grow or deepen their work. This model is designed for
              funders who want Firetree's judgment to guide deployment.
            </p>
            <p data-id="element-329">
              For funders who prefer to engage with a specific organisation or
              geography, we facilitate direct funding relationships, including
              introductions, visits, and ongoing dialogue. In both cases,
              Firetree continues to advocate for unrestricted, long-term giving.
            </p>
          </div>
  }, {
    id: 'q12',
    question: 'How does Firetree avoid creating dependency?',
    answer: <div className="space-y-4" data-id="element-330">
            <p data-id="element-331">
              We encourage portfolio organisations to maintain diversified
              funding sources. Firetree's own funding guidelines discourage any
              single donor from representing more than 25% of an established
              organisation's income. Contributions through the Fund are intended
              to strengthen systems and resilience, not create reliance on a
              single source.
            </p>
            <p data-id="element-332">
              In exceptional crisis situations, such as COVID-19, temporary
              concentrated support may be appropriate, but this is not the
              standard model.
            </p>
          </div>
  }]
}, {
  id: 'diligence',
  title: 'DUE DILIGENCE AND IMPACT',
  items: [{
    id: 'q13',
    question: 'Why does practitioner experience matter in child protection?',
    answer: <div className="space-y-4" data-id="element-333">
            <p data-id="element-334">
              Child protection is technical work. The quality of case
              management, trauma-informed care, safe reintegration decisions,
              and coordination with legal and government authorities is
              difficult to evaluate from documentation alone.
            </p>
            <p data-id="element-335">
              Because Firetree operates and funds child protection programmes
              directly, including through TCI in Manila, advised by a senior
              technical advisor with over 40 years of frontline experience, we
              assess partners with a practical understanding of what
              high-quality protection work requires. This practitioner-informed
              lens strengthens portfolio integrity.
            </p>
          </div>
  }, {
    id: 'q14',
    question: 'How does Firetree assess and measure impact across the portfolio?',
    answer: <div className="space-y-4" data-id="element-336">
            <p data-id="element-337">
              Firetree does not impose a standardised metrics framework across
              all organisations. We recognise that the populations these
              organisations serve are among the most marginalised, and that
              measurement in child protection and community development requires
              nuance, not just numbers.
            </p>
            <p data-id="element-338">
              Our approach to understanding impact is built on sustained
              engagement through visits, operational conversations, review of
              internal reporting, and participation in key discussions. We look
              for organisations that have honest learning cultures, adapt their
              models based on what they observe, and can articulate both what is
              working and what is not.
            </p>
            <p data-id="element-339">
              For funders who require specific outcome data, we are happy to
              discuss what each portfolio organisation tracks and how they
              report against their own objectives. We also share the standard
              annual reports of each organisation with all funders.
            </p>
          </div>
  }, {
    id: 'q15',
    question: 'Are the organisations in the portfolio externally audited?',
    answer: <p data-id="element-340">
            We do not mandate external audits beyond what the regulations and
            compliance requirements of each organisation's country of operation
            already require. Where an audit is required by law or by the
            organisation's own governance framework, we review it as part of our
            ongoing engagement. The strength of financial management systems and
            internal controls is something we assess closely regardless of
            whether a formal external audit is in place.
          </p>
  }]
}, {
  id: 'working',
  title: 'WORKING WITH FIRETREE AS A FUNDER',
  items: [{
    id: 'q16',
    question: 'What can funders expect in terms of communication and reporting?',
    answer: <div className="space-y-4" data-id="element-341">
            <p data-id="element-342">
              Funders receive a communication outlining how contributions have
              been allocated across the portfolio. Alongside this, we share each
              organisation's annual report and any other formal reporting that
              organisations provide to their funders.
            </p>
            <p data-id="element-343">
              In addition, funders receive an annual perspective from the
              Firetree team based on our direct interactions, visits, and
              conversations with portfolio organisations throughout the year.
              This is not a summary of documents. It is our view, grounded in
              the relationships we maintain with these organisations day to day.
            </p>
          </div>
  }, {
    id: 'q17',
    question: 'Can I visit the organisations myself?',
    answer: <div className="space-y-4" data-id="element-344">
            <p data-id="element-345">
              Where there is a specific need or interest, we work together with
              the organisation to decide whether a visit is appropriate and
              facilitate it accordingly. In cases where a direct relationship
              has been established between a funder and a portfolio
              organisation, we would prefer the organisation to make the
              decision on visits independently.
            </p>
            <p data-id="element-346">
              That said, the Firetree team visits portfolio organisations
              regularly as part of how we work. Where it makes sense, we are
              happy to open those visits to funders who wish to accompany us.
              This can often be the most natural and least disruptive way to see
              the work firsthand and understand it in context.
            </p>
          </div>
  }]
}];
export function EngagePage() {
  const [openId, setOpenId] = useState<string | null>(null);
  const toggleQuestion = (id: string) => {
    setOpenId(openId === id ? null : id);
  };
  const pathways = [{
    title: 'Allocate',
    description: 'Funds are housed with the Asia Community Foundation and directed by Firetree across the portfolio. Allocation decisions are based on ongoing engagement with each organisation, their current needs, and their potential to grow or deepen their work. Funders receive structured communication on how funds are directed, and an annual supplementary report grounded in our visits and engagement.',
    bgColor: '#d92b25',
    image: 'https://cdn.magicpatterns.com/uploads/jgYPwEwSW21qMnAQFD1jtC/Mlop_Tapang_1_(1).png',
    credit: "Photo: M'Lop Tapang"
  }, {
    title: 'Advise',
    description: 'For funders who want to deploy philanthropic capital on the issues and geographies we know deeply, Firetree can share its perspective on where funding will be most impactful. Drawing on years of direct experience working with and within these organisations, we help funders make decisions that are grounded, contextual, and built for the long term. Funders can also request access to the same portfolio reports we produce, giving them the same visibility into the organisations and their work.',
    bgColor: '#243462',
    image: 'https://i.postimg.cc/yNSpfFCH/DB46C576-433B-4013-8EA9-980371D85B83-(1).jpg',
    credit: '© Firetree Fund'
  }, {
    title: 'Connect',
    description: 'For funders who want to engage more closely with specific organisations or prefer to give directly to particular programmes or geographies, Firetree facilitates introductions, visits and continued dialogue. We provide the context we have built over years of partnership and stay available throughout to advocate for the kind of funding these organisations need most.',
    bgColor: '#2B5E8C',
    image: 'https://i.postimg.cc/T3sHqtFP/MS-2.jpg',
    credit: 'Photo: Mith Samlanh'
  }];
  return <div className="pb-24" data-id="element-347">
      <PageBanner title="Engage with us" subtitle="Participate with the Firetree Fund." image="https://cdn.magicpatterns.com/uploads/6AgEBbgzfQNi3QtWbyDbvM/Teach_for_the_Philippines_-_Website_Banner_-_Fellowship_Program_(VVES).jpg" credit="Photo: Teach for the Philippines" data-id="element-348" />

      {/* Our Principles Section */}
      <section className="py-10 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-349">
        <div className="max-w-3xl mb-8" data-id="element-350">
          <h2 className="text-3xl font-serif text-brand-charcoal mb-3" data-id="element-351">
            Our principles
          </h2>
          <p className="text-xl text-brand-brown leading-relaxed" data-id="element-352">
            Our funding is built on three principles. These principles are not
            aspirational but the conditions under which we believe meaningful,
            lasting impact is possible.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-353">
          {/* Principle 1 */}
          <div className="bg-brand-cream p-6 rounded-none sm:rounded-2xl shadow-sm border border-brand-shape/50 flex flex-col items-center text-center" data-id="element-354">
            <div className="w-12 h-12 rounded-full bg-brand-card flex items-center justify-center mb-4 icon-hover" data-id="element-355">
              <Handshake className="w-5 h-5 text-brand-gold" strokeWidth={1} data-id="element-356" />
            </div>
            <h3 className="text-xl font-serif text-brand-charcoal mb-3" data-id="element-357">
              Relationship-based
            </h3>
            <p className="text-brand-brown leading-relaxed" data-id="element-358">
              Trust is the foundation of everything we do. We only fund
              organisations we know deeply, in geographies where we have
              extensive direct experience.
            </p>
          </div>

          {/* Principle 2 */}
          <div className="bg-brand-cream p-6 rounded-none sm:rounded-2xl shadow-sm border border-brand-shape/50 flex flex-col items-center text-center" data-id="element-359">
            <div className="w-12 h-12 rounded-full bg-brand-card flex items-center justify-center mb-4 icon-hover" data-id="element-360">
              <Clock className="w-5 h-5 text-brand-gold" strokeWidth={1} data-id="element-361" />
            </div>
            <h3 className="text-xl font-serif text-brand-charcoal mb-3" data-id="element-362">
              Long-term
            </h3>
            <p className="text-brand-brown leading-relaxed" data-id="element-363">
              We support our partners for as long as they need it, in service
              that grows. Short funding cycles consume the energy that leaders
              should be spending on their work.
            </p>
          </div>

          {/* Principle 3 */}
          <div className="bg-brand-cream p-6 rounded-none sm:rounded-2xl shadow-sm border border-brand-shape/50 flex flex-col items-center text-center" data-id="element-364">
            <div className="w-12 h-12 rounded-full bg-brand-card flex items-center justify-center mb-4 icon-hover" data-id="element-365">
              <Unlock className="w-5 h-5 text-brand-gold" strokeWidth={1} data-id="element-366" />
            </div>
            <h3 className="text-xl font-serif text-brand-charcoal mb-3" data-id="element-367">
              Unrestricted
            </h3>
            <p className="text-brand-brown leading-relaxed" data-id="element-368">
              Once trust is established, we believe the organisation is
              considerably better positioned to decide how resources are best
              deployed. We do not prescribe how funding is used.
            </p>
          </div>
        </div>
      </section>

      {/* Three Pathways Section — Full Width Arrow design */}
      <section className="py-12 bg-brand-card/30 mt-12" data-id="element-369">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-10" data-id="element-370">
          <div className="max-w-3xl" data-id="element-371">
            <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-6" data-id="element-372">
              Ways to participate
            </h2>
            <p className="text-lg text-brand-brown leading-relaxed" data-id="element-373">
              Whether you want to join the philanthropic fund or engage directly
              with specific organisations, we can have various ways in which we
              work with fellow funders.
            </p>
          </div>
        </div>

        {/* Mobile: stacked cards / Desktop: arrow panels */}

        {/* Mobile layout */}
        <div className="md:hidden px-4 sm:px-6 space-y-4" data-id="element-374">
          {pathways.map((pathway, index) => <motion.div key={pathway.title} initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-40px'
        }} transition={{
          duration: 0.6,
          delay: index * 0.1
        }} className="rounded-none sm:rounded-xl overflow-hidden" data-id="element-375">
              <PhotoCredit credit={pathway.credit} className="relative h-48" data-id="element-376">
                <img src={pathway.image} alt="" className="w-full h-full object-cover" data-id="element-377" />
                <div className="absolute inset-0 bg-black/10" data-id="element-378" />
              </PhotoCredit>
              <div className="p-6 text-white" style={{
            backgroundColor: pathway.bgColor
          }} data-id="element-379">
                <h3 className="text-xl font-serif mb-3" data-id="element-380">{pathway.title}</h3>
                <p className="leading-relaxed text-sm text-white/90" data-id="element-381">
                  {pathway.description}
                </p>
              </div>
            </motion.div>)}
        </div>

        {/* Desktop layout — arrow panels */}
        <div className="hidden md:grid w-full" style={{
        gridAutoRows: '1fr'
      }} data-id="element-382">
          {pathways.map((pathway, index) => <div key={pathway.title} className="relative w-full flex flex-row" data-id="element-383">
              <motion.div initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 1,
            ease: [0.25, 0.1, 0.25, 1],
            delay: index * 0.15
          }} className="relative z-10 w-[50%] lg:w-[45%] flex flex-col justify-center text-white pr-16 lg:pr-20" style={{
            paddingTop: '12px',
            paddingBottom: '12px',
            backgroundColor: pathway.bgColor,
            paddingLeft: 'max(1rem, calc((100vw - 80rem) / 2 + 2rem))'
          }} data-id="element-384">
                <motion.div initial={{
              opacity: 0,
              y: 12
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-80px'
            }} transition={{
              duration: 0.8,
              ease: [0.25, 0.1, 0.25, 1],
              delay: index * 0.15 + 0.4
            }} data-id="element-385">
                  <h3 className="text-2xl md:text-3xl font-serif mb-3" data-id="element-386">
                    {pathway.title}
                  </h3>
                  <p className="leading-relaxed text-sm md:text-base text-white/90" data-id="element-387">
                    {pathway.description}
                  </p>
                </motion.div>

                {/* Arrow motif — pointing right into the image */}
                <div className="absolute right-0 top-0 h-full" style={{
              transform: 'translateX(100%)'
            }} data-id="element-388">
                  <svg viewBox="0 0 48 100" preserveAspectRatio="none" className="h-full w-10 lg:w-12" style={{
                display: 'block'
              }} data-id="element-389">
                    <polygon points="0,0 48,50 0,100" fill={pathway.bgColor} data-id="element-390" />
                  </svg>
                </div>
              </motion.div>

              <PhotoCredit credit={pathway.credit} className="flex-1 relative overflow-hidden" data-id="element-391">
                <img src={pathway.image} alt="" className="absolute inset-0 w-full h-full object-cover object-center" data-id="element-392" />
              </PhotoCredit>
            </div>)}
        </div>
      </section>

      {/* Full FAQ Section */}
      <section id="faq" className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-393">
        <AnimatedSection data-id="element-394">
          <h2 className="text-3xl font-serif text-brand-charcoal mb-16 text-center" data-id="element-395">
            Frequently Asked Questions
          </h2>
        </AnimatedSection>

        <div className="space-y-16" data-id="element-396">
          {faqData.map(category => <AnimatedSection key={category.id} data-id="element-397">
              <h3 className="text-sm font-bold tracking-widest uppercase text-brand-gold mb-6" data-id="element-398">
                {category.title}
              </h3>

              <div className="border-t border-brand-shape/50" data-id="element-399">
                {category.items.map(item => {
              const isOpen = openId === item.id;
              return <div key={item.id} className="border-b border-brand-shape/50" data-id="element-400">
                      <button onClick={() => toggleQuestion(item.id)} className="w-full py-6 flex justify-between items-center text-left focus:outline-none group" aria-expanded={isOpen} data-id="element-401">
                        <span className="text-lg font-semibold text-brand-charcoal pr-8 group-hover:text-brand-red transition-colors" data-id="element-402">
                          {item.question}
                        </span>
                        <span className="flex-shrink-0 text-brand-gold" data-id="element-403">
                          {isOpen ? <ChevronUp className="w-6 h-6" data-id="element-404" /> : <ChevronDown className="w-6 h-6" data-id="element-405" />}
                        </span>
                      </button>

                      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[1000px] opacity-100 pb-8' : 'max-h-0 opacity-0'}`} data-id="element-406">
                        <div className="text-brand-brown leading-relaxed text-lg" data-id="element-407">
                          {item.answer}
                        </div>
                      </div>
                    </div>;
            })}
              </div>
            </AnimatedSection>)}
        </div>
      </section>
    </div>;
}